import { faArrowDown, faArrowsRotate, faBars } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { APP_CONFIG } from '@config';
import { SidebarTriggerIcon } from '@shared/components/ui/icons';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from '@shared/components/ui/sidebar';
import { PULL_TO_REFRESH_THRESHOLD, usePullToRefresh } from '@shared/hooks/usePullToRefresh';

import { NotificationCenter } from '@modules/Notifications';
import { useRadixPortalStore } from '@shared/stores/radixPortalStore';
import { Breadcrumbs } from './Breadcrumbs';
import { GlobalSearch } from './GlobalSearch';
import { HeaderActions } from './HeaderActions';
import { locksViewportScrollForPath } from './locksViewportScrollForPath';
import MenuItem from './MenuItem';
import { UserFooterItem } from './UserFooterItem';
import { useNavigation } from './useNavigation';

interface SidebarTriggerWithIconProps {
  className?: string;
}

function SidebarTriggerWithIcon({ className }: SidebarTriggerWithIconProps) {
  const { isMobile } = useSidebar();

  const icon = isMobile ? <FontAwesomeIcon icon={faBars} className="!w-4 !h-4" /> : <SidebarTriggerIcon />;

  return (
    <SidebarTrigger
      className={`self-start hover:bg-accent hover:text-text transition-colors duration-200 ${className}`}
      data-testid="sidebar-trigger"
      icon={icon}
    />
  );
}

interface NavigationSidebarProps {
  children: React.ReactNode;
  className?: string;
}

function NavigationSidebarContent({ children, className }: NavigationSidebarProps) {
  const location = useLocation();
  const { isMobile } = useSidebar();
  const queryClient = useQueryClient();
  const isConfigurationRoute = location.pathname.startsWith('/configuration');
  // Pantallas admin a viewport fijo: el scroll vive dentro de la tabla, no en el shell.
  // Presets de reglas: la página debe scrollear (barra en el main, no pegada al listado).
  const locksViewportScroll = locksViewportScrollForPath(location.pathname);
  const prevLocationRef = React.useRef(location.pathname);
  const contentScrollRef = React.useRef<HTMLDivElement>(null);
  const setCloseAllPortals = useRadixPortalStore((s) => s.setCloseAllPortals);
  const { menuItems, sidebarFooterItems, showConfigMenu, pageTitle, breadcrumbs, headerActions } = useNavigation();

  const handlePullRefresh = React.useCallback(async () => {
    await queryClient.invalidateQueries({ refetchType: 'active' });
  }, [queryClient]);

  const { pullDistance, isRefreshing, isPulling } = usePullToRefresh({
    containerRef: contentScrollRef,
    onRefresh: handlePullRefresh,
    enabled: isMobile,
  });

  // Cerrar todos los portales de Radix antes del unmount para evitar removeChild error
  React.useEffect(() => {
    if (prevLocationRef.current !== location.pathname) {
      prevLocationRef.current = location.pathname;
      setCloseAllPortals(true);
    }
  }, [location.pathname, setCloseAllPortals]);

  // 🎯 Los iconos ya vienen envueltos con las clases correctas desde menuConfig
  // No necesitamos calcular iconColorClass e iconColorStyle aquí

  const pullIndicatorHeight = isRefreshing ? 48 : pullDistance;
  const pullReady = pullDistance >= PULL_TO_REFRESH_THRESHOLD || isRefreshing;

  return (
    <div className={`flex h-screen w-full font-lato text-title ${className}`}>
      <Sidebar collapsible="icon" className="border-card-border">
        <SidebarContent className="flex flex-col h-full pt-2 bg-body-bg light:bg-card-bg">
          {/* Logo y dropdown solo en menú principal - Fijo en la parte superior */}
          {!showConfigMenu && (
            <div className="flex-shrink-0">
              <SidebarHeader className="py-0">
                <SidebarMenu>
                  <SidebarMenuItem key="logo">
                    <div className="flex items-center w-full group-data-[collapsible=icon]:block">
                      {/* Logo (incluye el nombre) - alineado con el menú */}
                      <div className="flex items-center flex-1 py-2 px-2 group-data-[collapsible=icon]:py-0 group-data-[collapsible=icon]:px-0">
                        <div className="flex items-center justify-start min-w-0 flex-1 group-data-[collapsible=icon]:flex-none group-data-[collapsible=icon]:w-4 group-data-[collapsible=icon]:h-4 group-data-[collapsible=icon]:mt-2 group-data-[collapsible=icon]:mb-2 group-data-[collapsible=icon]:justify-center overflow-hidden">
                          <img
                            src="/imagen/logo_beeping.svg"
                            alt={APP_CONFIG.appName}
                            className="h-6 w-auto object-contain block dark:hidden group-data-[collapsible=icon]:h-4 group-data-[collapsible=icon]:min-w-0"
                          />
                          <img
                            src="/imagen/logo_beeping_white.svg"
                            alt={APP_CONFIG.appName}
                            className="h-6 w-auto object-contain hidden dark:block group-data-[collapsible=icon]:h-4 group-data-[collapsible=icon]:min-w-0"
                          />
                        </div>
                      </div>

                      {/* Campana de notificaciones */}
                      <div className="group-data-[collapsible=icon]:hidden">
                        <NotificationCenter />
                      </div>
                    </div>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarHeader>

              {/* Búsqueda global - Global search - Fija en la parte superior */}
              <div className="px-2 mt-2 mb-2 group-data-[collapsible=icon]:hidden">
                <GlobalSearch />
              </div>
            </div>
          )}

          {/* Contenido scrolleable: Menú principal */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {/* Menú principal */}
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {menuItems.map((item) => (
                    <MenuItem key={`${showConfigMenu ? 'config' : 'main'}-${item.id}`} item={item} />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </div>

          {/* Footer - Fijo en la parte inferior */}
          <SidebarFooter className="mt-auto flex-shrink-0">
            {!showConfigMenu && (
              <>
                {sidebarFooterItems.length > 0 && (
                  <SidebarMenu>
                    {sidebarFooterItems.map((item) => (
                      <MenuItem key={`footer-${item.id}`} item={item} />
                    ))}
                  </SidebarMenu>
                )}
                {sidebarFooterItems.length > 0 && <SidebarSeparator />}
                <SidebarMenu>
                  <UserFooterItem />
                </SidebarMenu>
              </>
            )}
          </SidebarFooter>
        </SidebarContent>
      </Sidebar>

      {/* Contenido principal */}
      <main className="flex-1 flex flex-col overflow-x-hidden">
        {isConfigurationRoute ? (
          <div className="flex items-center px-4 py-3 bg-body-bg border-b border-card-border sm:hidden">
            <SidebarTriggerWithIcon className="text-text shrink-0" />
          </div>
        ) : (
          <div className="flex items-center justify-between px-4 py-3 bg-body-bg border-b border-card-border text-sm text-text">
            <div className="flex items-center min-w-0 flex-1 mr-4">
              <SidebarTriggerWithIcon className="text-text shrink-0" />
              <div className="sm:border-r border-complementary-text ml-1 sm:mr-2 h-3 shrink-0" />
              <div className="min-w-0 overflow-hidden">
                {breadcrumbs.length > 0 ? (
                  <Breadcrumbs items={breadcrumbs} />
                ) : (
                  <h1 className="font-normal truncate">{pageTitle}</h1>
                )}
              </div>
            </div>

            {/* 🎯 Acciones del header */}
            <HeaderActions actions={headerActions} />
          </div>
        )}
        <div
          ref={contentScrollRef}
          className={`flex min-h-0 flex-1 flex-col ${locksViewportScroll ? 'overflow-hidden' : 'overflow-y-auto'}`}
          data-testid="navigation-content-scroll"
        >
          {isMobile && isPulling && (
            <div
              className="pointer-events-none flex shrink-0 items-center justify-center overflow-hidden text-complementary-text transition-colors"
              style={{ height: pullIndicatorHeight }}
              aria-hidden={!isRefreshing}
              data-testid="pull-to-refresh-indicator"
            >
              <FontAwesomeIcon
                icon={isRefreshing ? faArrowsRotate : faArrowDown}
                className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
                style={
                  isRefreshing
                    ? undefined
                    : {
                        transform: `rotate(${pullReady ? 180 : Math.min(pullDistance / PULL_TO_REFRESH_THRESHOLD, 1) * 180}deg)`,
                        transition: 'transform 80ms linear',
                      }
                }
              />
            </div>
          )}
          {isConfigurationRoute ? (
            <div
              className={`mx-auto flex w-full max-w-5xl flex-1 flex-col px-6 pt-8 pb-8 ${locksViewportScroll ? 'min-h-0' : ''}`}
            >
              {children}
            </div>
          ) : locksViewportScroll ? (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">{children}</div>
          ) : (
            children
          )}
        </div>
      </main>
    </div>
  );
}

export default function NavigationSidebar({ children, className }: NavigationSidebarProps) {
  return (
    <SidebarProvider>
      <NavigationSidebarContent className={className}>{children}</NavigationSidebarContent>
    </SidebarProvider>
  );
}
